import { ref, watch, onBeforeUnmount, getCurrentInstance } from "vue";
import { attachTiltEffect } from "../utils/tilt-effect";

/**
 * @description A composable function that sets up and manages visual effects for a given set of HTML elements based on the device type (desktop or mobile).
 * It applies a tilt effect on desktop.
 *
 * @param {HTMLElement[]} targets - An array of HTML elements to which the effects will be applied.
 * @param {() => boolean} isDesktop - A reactive function that returns a boolean indicating whether the device is a desktop.
 *
 * @returns {{ setupEffects: () => void }} An object containing a function to manually trigger the setup of effects.
 *
 * @example
 * ```typescript
 * const gameCards = Array.from(document.querySelectorAll(".games-card-game"));
 * const isDesktop = useBreakpoints("sm2");
 * const { setupEffects } = useGameEffects(gameCards, () => isDesktop.value);
 *
 * // Manually trigger the setup of effects (e.g., after dynamically adding new targets)
 * setupEffects();
 * ```
 */
export function useGameEffects(targets: HTMLElement[], isDesktop: () => boolean) {
    if (!targets) return;
    const cleanupFunctions = ref<(() => void)[]>([]);

    function cleanup() {
        cleanupFunctions.value.forEach((fn) => fn());
        cleanupFunctions.value = [];
    }

    /**
     * Sets up visual effects for the game elements based on the device type.
     *
     * @param tiltX - The tilt angle on the X-axis for the tilt effect (default: 35).
     * @param tiltY - The tilt angle on the Y-axis for the tilt effect (default: 35).
     */
    function setupEffects(tiltX = 35, tiltY = 35) {
        cleanup();
        if (targets.length && isDesktop()) {
            cleanupFunctions.value.push(attachTiltEffect(targets, tiltX, tiltY, 4, 1.03));
        }
    }

    watch(isDesktop, () => setupEffects(), { immediate: true });
    if (getCurrentInstance()) onBeforeUnmount(cleanup);

    return { setupEffects };
}
